import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { isQuietError, memberPath, memberRolePath, orgPath, seatReleasePath, useLicApi } from './licApi';
import {
  BEIGE,
  ConfirmDialog,
  EmptyRow,
  HEADING,
  LoadingBox,
  MUTED,
  Section,
  StatusChip,
  ViewHeader,
  tableContainerSx,
  useCodeConfirmation,
} from './OrgCommon';
import { formatDateTime, isEmail, parseDomainList } from './orgFormat';

const ROLES = ['User', 'Admin'];

const sameList = (a, b) => a.length === b.length && a.every((item, index) => item === b[index]);

/** Allowed domains, plus the org's member list (extra emails and admins). */
const PeopleView = ({ org, myEmail, notify, onOrgChanged }) => {
  const { licFetch, superAdmin } = useLicApi();
  const { run, codeDialog } = useCodeConfirmation();
  const savedDomains = useMemo(() => (Array.isArray(org.allowedDomains) ? org.allowedDomains : []), [org.allowedDomains]);
  const [domains, setDomains] = useState(savedDomains);
  const [domainInput, setDomainInput] = useState('');
  const [domainError, setDomainError] = useState('');
  const [savingDomains, setSavingDomains] = useState(false);

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('User');
  const [adding, setAdding] = useState(false);

  const [pending, setPending] = useState(null); // { kind: 'revoke' | 'role' | 'remove', member }
  const [busyEmail, setBusyEmail] = useState('');

  useEffect(() => {
    setDomains(savedDomains);
  }, [savedDomains]);

  const loadMembers = useCallback(async () => {
    setError('');
    try {
      const result = await licFetch(orgPath(org.id, '/members'));
      setMembers(Array.isArray(result.members) ? result.members : []);
    } catch (loadError) {
      if (loadError.status !== 401) setError(loadError.message);
    }
  }, [licFetch, org.id]);

  useEffect(() => {
    loadMembers().finally(() => setLoading(false));
  }, [loadMembers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadMembers(), onOrgChanged()]);
    setRefreshing(false);
  };

  // ---------------------------------------------------------------- domains

  const addDomain = () => {
    const parsed = parseDomainList(domainInput);
    if (!parsed.length) {
      setDomainError(domainInput.trim() ? 'Enter a domain like acme.com.' : '');
      return;
    }
    setDomains((current) => Array.from(new Set([...current, ...parsed])));
    setDomainInput('');
    setDomainError('');
  };

  const saveDomains = async () => {
    setSavingDomains(true);
    try {
      const result = await licFetch(orgPath(org.id, '/domains'), { method: 'PUT', body: { allowedDomains: domains } });
      if (Array.isArray(result.allowedDomains)) setDomains(result.allowedDomains);
      notify('Allowed domains saved.');
      await onOrgChanged();
    } catch (saveError) {
      if (saveError.status !== 401) notify(saveError.message, 'error');
    } finally {
      setSavingDomains(false);
    }
  };

  const domainsDirty = !sameList([...domains].sort(), [...savedDomains].sort());

  // ---------------------------------------------------------------- members

  const upsertMember = async (email, role, successMessage) => {
    const result = await licFetch(orgPath(org.id, '/members'), { method: 'POST', body: { email, role } });
    notify(successMessage);
    await loadMembers();
    return result;
  };

  const handleAdd = async (event) => {
    event.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!isEmail(email)) return;
    setAdding(true);
    try {
      await upsertMember(email, newRole, `${email} added as ${newRole}.`);
      setNewEmail('');
      setNewRole('User');
    } catch (addError) {
      if (addError.status !== 401) notify(addError.message, 'error');
    } finally {
      setAdding(false);
    }
  };

  const handleUnblock = async (member) => {
    setBusyEmail(member.email);
    try {
      await upsertMember(member.email, member.role || 'User', `${member.email} unblocked.`);
    } catch (unblockError) {
      if (unblockError.status !== 401) notify(unblockError.message, 'error');
    } finally {
      setBusyEmail('');
    }
  };

  // Revoke, role change and remove. Acting on another IT admin asks for a code emailed to you (see useCodeConfirmation).
  const handleConfirm = async () => {
    if (!pending) return;
    const { kind, member } = pending;
    setBusyEmail(member.email);
    try {
      if (kind === 'revoke') {
        await run((headers) => licFetch(seatReleasePath(org.id, member.email), { method: 'POST', headers }));
        notify(`Revoked ${member.email}'s license.`);
      } else if (kind === 'role') {
        const role = member.role === 'Admin' ? 'User' : 'Admin';
        await run((headers) => licFetch(memberRolePath(org.id, member.email), { method: 'PUT', body: { role }, headers }));
        notify(`${member.email} is now ${role === 'Admin' ? 'an Admin' : 'a User'}.`);
      } else {
        await run((headers) => licFetch(memberPath(org.id, member.email), { method: 'DELETE', headers }));
        notify(`${member.email} removed.`);
      }
      setPending(null);
      await Promise.all([loadMembers(), onOrgChanged()]);
    } catch (actionError) {
      if (actionError.code === 'Cancelled') setPending(null);
      else if (!isQuietError(actionError)) notify(actionError.message, 'error');
    } finally {
      setBusyEmail('');
    }
  };

  const isMe = (email) => Boolean(myEmail) && String(email || '').toLowerCase() === myEmail.toLowerCase();

  const confirmDetails = () => {
    if (!pending) return { title: '', label: '', text: '' };
    const { kind, member } = pending;
    const isAdmin = member.role === 'Admin';
    const codeNote = isAdmin && !superAdmin ? ' They are an IT admin, so you will confirm with a code sent to your email.' : '';
    if (kind === 'revoke') {
      return {
        title: 'Revoke this license?',
        label: 'Revoke license',
        text: `${member.email} will lose HUBAROC at their next license check (within 24 h) and the license becomes free. They can take a free license again the next time they sign in.${codeNote}`,
      };
    }
    if (kind === 'role') {
      return isAdmin
        ? {
            title: 'Make this person a User?',
            label: 'Make user',
            text: `${member.email} will no longer be able to sign in to this dashboard. They keep using their license.${codeNote}`,
          }
        : {
            title: 'Make this person an Admin?',
            label: 'Make admin',
            color: 'primary',
            text: `${member.email} will be able to sign in to this dashboard and manage licenses and people.`,
          };
    }
    return {
      title: 'Remove this person?',
      label: 'Remove',
      text: `${member.email} will be removed and lose their license. If their email is on an allowed domain they can still sign in again.${codeNote}`,
    };
  };
  const confirm = confirmDetails();

  return (
    <Box>
      <ViewHeader title="People" onRefresh={handleRefresh} refreshing={refreshing} />

      <Typography variant="body2" sx={{ color: MUTED, mb: 3 }}>
        People on an allowed domain can sign in with the org code. Add other emails here. Revoking a license frees it; the
        person can take a free one again the next time they sign in. The organization always keeps at least one admin, and
        changes to another admin are confirmed with a code sent to your email.
      </Typography>

      <Section title="Allowed domains">
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, minHeight: 32 }}>
          {domains.length === 0 ? (
            <Typography variant="body2" sx={{ color: MUTED }}>
              No domains. Only the people listed below can sign in.
            </Typography>
          ) : (
            domains.map((domain) => (
              <Chip
                key={domain}
                label={`@${domain}`}
                onDelete={() => setDomains((current) => current.filter((item) => item !== domain))}
                sx={{ backgroundColor: BEIGE, color: HEADING, fontWeight: 500 }}
              />
            ))
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            size="small"
            label="Add domain"
            placeholder="acme.com"
            value={domainInput}
            error={Boolean(domainError)}
            helperText={domainError || ' '}
            onChange={(event) => {
              setDomainInput(event.target.value);
              setDomainError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addDomain();
              }
            }}
            sx={{ minWidth: 220 }}
          />
          <Button variant="outlined" onClick={addDomain} disabled={!domainInput.trim()} sx={{ textTransform: 'none', height: 40 }}>
            Add
          </Button>
          <Button variant="contained" onClick={saveDomains} disabled={!domainsDirty || savingDomains}>
            {savingDomains ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
          {domainsDirty && !savingDomains && (
            <Button onClick={() => setDomains(savedDomains)} sx={{ textTransform: 'none', height: 40 }}>
              Undo changes
            </Button>
          )}
        </Box>
      </Section>

      <Section title="Add a person">
        <Box component="form" onSubmit={handleAdd} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            type="email"
            label="Email"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            sx={{ flex: '1 1 240px', maxWidth: 360 }}
          />
          <TextField select size="small" label="Role" value={newRole} onChange={(event) => setNewRole(event.target.value)} sx={{ width: 130 }}>
            {ROLES.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </TextField>
          <Button type="submit" variant="contained" disabled={adding || !isEmail(newEmail)}>
            {adding ? <CircularProgress size={20} color="inherit" /> : 'Add'}
          </Button>
        </Box>
        <Typography variant="body2" sx={{ color: MUTED, mt: 1 }}>
          Admins can sign in to this dashboard. Users can only use a license.
        </Typography>
      </Section>

      <Typography variant="h6" sx={{ fontWeight: 600, color: HEADING, mb: 1 }}>
        Members
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <LoadingBox />
      ) : (
        <Box sx={tableContainerSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Has seat</TableCell>
                <TableCell align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.length === 0 ? (
                <EmptyRow colSpan={5}>No members yet.</EmptyRow>
              ) : (
                members.map((member) => {
                  const blocked = member.status === 'Blocked';
                  const busy = busyEmail === member.email;
                  const added = [member.addedBy && `Added by ${member.addedBy}`, member.addedAtUtc && formatDateTime(member.addedAtUtc)]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <TableRow key={member.email} hover>
                      <TableCell sx={{ wordBreak: 'break-all' }}>
                        <Tooltip title={added}>
                          <span>{member.email}</span>
                        </Tooltip>
                        {isMe(member.email) && (
                          <Typography component="span" variant="body2" sx={{ color: MUTED, ml: 1 }}>
                            (you)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{member.role || 'User'}</TableCell>
                      <TableCell>
                        <StatusChip status={blocked ? 'Blocked' : 'Active'} />
                      </TableCell>
                      <TableCell>{member.hasSeat ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {busy ? (
                          <CircularProgress size={18} />
                        ) : isMe(member.email) ? null : (
                          <>
                            {blocked ? (
                              <Button size="small" sx={{ textTransform: 'none' }} onClick={() => handleUnblock(member)}>
                                Unblock
                              </Button>
                            ) : (
                              <>
                                {member.hasSeat && (
                                  <Button size="small" color="warning" sx={{ textTransform: 'none' }} onClick={() => setPending({ kind: 'revoke', member })}>
                                    Revoke license
                                  </Button>
                                )}
                                <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setPending({ kind: 'role', member })}>
                                  {member.role === 'Admin' ? 'Make user' : 'Make admin'}
                                </Button>
                              </>
                            )}
                            <Button size="small" color="error" sx={{ textTransform: 'none' }} onClick={() => setPending({ kind: 'remove', member })}>
                              Remove
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <ConfirmDialog
        open={Boolean(pending)}
        title={confirm.title}
        confirmLabel={confirm.label}
        confirmColor={confirm.color}
        busy={Boolean(busyEmail)}
        onConfirm={handleConfirm}
        onClose={() => setPending(null)}
      >
        {confirm.text}
      </ConfirmDialog>
      {codeDialog}
    </Box>
  );
};

export default PeopleView;
